"use client";

import { useParams } from "next/navigation";
import { useQuery } from "@tanstack/react-query";

import { getOrg } from "@/actions/orgs";
import { isValidOrgId } from "@/lib/utils";

/**
 * Route-level validator component that checks orgId access
 * If the orgId throws a 403, it will trigger the error boundary
 * to show the proper error page instead of just a toast message
 *
 * This component throws errors during render to trigger the error boundary,
 * which is the proper way to handle route-level authorization errors.
 */
export function RouteOrgValidator() {
  const params = useParams();
  const orgId = params?.orgId as string | undefined;

  const shouldValidate = isValidOrgId(orgId);

  useQuery({
    queryKey: ["route-org-validation", orgId],
    queryFn: async () => {
      if (!isValidOrgId(orgId)) {
        return null;
      }
      // This will throw ApiError with status 403 if user doesn't have access
      return await getOrg(orgId);
    },
    enabled: shouldValidate,
    retry: false, // Don't retry 403 errors
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    // Throw 403 errors to trigger error boundary automatically
    throwOnError: (error) => {
      const errorObj = error as unknown as Record<string, unknown> & { status?: number };
      const is403 = errorObj?.status === 403;
      // Throw 403s to trigger error boundary, don't throw other errors
      return is403 === true;
    },
  });

  // This component doesn't render anything when validation passes
  return null;
}
